import { c as _c } from "react/compiler-runtime";
import { useSphereDialog } from "@components/common/dialog/sphere-dialog-provider";
import { Text } from "@flat-ui";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useAppParams } from "@router/router-helper";
import { refreshProjectAccess } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { hasUserValidCompanyRole } from "@utils/access-control/company/company-access-control";
import { formatUserRoleType } from "@utils/data-display";

/** A change of a member's project access: a new role, or their removal when no role is given */

/** Handles a project admin changing their own role in a project, or removing themselves from it */
export function useSelfProjectAccess(t0) {
  const $ = _c(17);
  const {
    companyId,
    projectId
  } = t0;
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(currentUserSelector);
  const {
    navigateToProjects
  } = useAppNavigation();
  const params = useAppParams();
  let t1;
  if ($[0] !== currentUser) {
    t1 = hasUserValidCompanyRole({
      currentUser,
      requiredCompanyRoles: ["COMPANY_EXECUTIVE"]
    });
    $[0] = currentUser;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isWorkspaceAdmin = t1;
  let t2;
  if ($[2] !== currentUser?.identity || $[3] !== isWorkspaceAdmin) {
    t2 = function isOwnChange(t3) {
      const {
        memberIdentity
      } = t3;
      return !isWorkspaceAdmin && memberIdentity === currentUser?.identity;
    };
    $[2] = currentUser?.identity;
    $[3] = isWorkspaceAdmin;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const isOwnChange = t2;
  let t3;
  if ($[5] !== isOwnChange) {
    t3 = function isLosingAccess(change) {
      const {
        currentRole,
        newRole
      } = change;
      return isOwnChange(change) && (newRole === undefined || currentRole === "RESOURCE_ADMIN" && newRole !== "RESOURCE_ADMIN");
    };
    $[5] = isOwnChange;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const isLosingAccess = t3;
  let t4;
  if ($[7] !== companyId || $[8] !== dispatch || $[9] !== isOwnChange || $[10] !== navigateToProjects || $[11] !== params || $[12] !== projectId) {
    t4 = async function onAccessChanged(change_0) {
      if (!isOwnChange(change_0)) {
        return;
      }
      try {
        await dispatch(refreshProjectAccess({
          companyId,
          projectId
        })).unwrap();
      } catch {
        if (params.projectId === projectId) {
          navigateToProjects({
            companyId
          });
        }
      }
    };
    $[7] = companyId;
    $[8] = dispatch;
    $[9] = isOwnChange;
    $[10] = navigateToProjects;
    $[11] = params;
    $[12] = projectId;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const onAccessChanged = t4;
  let t5;
  if ($[14] !== isLosingAccess || $[15] !== onAccessChanged) {
    t5 = {
      isLosingAccess,
      onAccessChanged
    };
    $[14] = isLosingAccess;
    $[15] = onAccessChanged;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}

/**
 * Returns how to ask the current user to confirm giving up being admin of a project.
 * The returned function resolves whether the role should be changed, which it should when there is
 * nothing to confirm.
 */
export function useConfirmSelfRoleChange(props) {
  const $ = _c(3);
  const {
    isLosingAccess
  } = useSelfProjectAccess(props);
  const {
    createDialog
  } = useSphereDialog();
  let t0;
  if ($[0] !== createDialog || $[1] !== isLosingAccess) {
    t0 = async change => {
      if (!change.newRole || !isLosingAccess(change)) {
        return true;
      }
      return createDialog({
        title: "Change your own role",
        confirmText: "Change my role"
      }, <Text variant="bodyM">You will be <strong>{formatUserRoleType(change.newRole)}</strong> of this project, so you may no longer be able to manage its members or settings. Only another project admin can make you admin again.</Text>);
    };
    $[0] = createDialog;
    $[1] = isLosingAccess;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}